<script setup>
import { ref, computed } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import ButtonCta from "@components/ButtonCta.vue";

const games = [
    {
        title: "Conquest Dark",
        category: "Action",
        thumb: "https://wishlisted.s3.amazonaws.com/images/games/conquest-dark-thumbnail.avif",
        video: "https://cloud.papercrowns.com/video/upload/q_auto/v1772219264/wishlisted/conquest.mp4",
    },
    {
        title: "Splodey",
        category: "Platformer",
        thumb: "https://wishlisted.s3.amazonaws.com/images/games/splodey-thumbnail.avif",
        video: "https://cloud.papercrowns.com/video/upload/q_auto/v1772219263/wishlisted/arsonate.mp4",
    },
    {
        title: "Hell Clock",
        category: "Action",
        thumb: "https://wishlisted.s3.amazonaws.com/images/games/hell-clock-thumbnail.avif",
        video: "https://cloud.papercrowns.com/video/upload/q_auto/v1772219262/wishlisted/arcane.mp4",
    },
];

const slides = [...games, ...games];

const activeIndex = ref(0);
const activeGame = computed(() => games[activeIndex.value]);

const swiperDesktop = ref(null);
const swiperMobile = ref(null);

function onDesktopInit(swiper) {
    swiperDesktop.value = swiper;
}

function onMobileInit(swiper) {
    swiperMobile.value = swiper;
}

function onSlideChange(swiper) {
    activeIndex.value = swiper.realIndex % games.length;
}

function goPrev() {
    if (swiperDesktop.value) swiperDesktop.value.slidePrev();
}

function goNext() {
    if (swiperDesktop.value) swiperDesktop.value.slideNext();
}
</script>

<template>
    <!-- ===== DESKTOP: right column with card/video + carousel ===== -->
    <div
        class="hidden md:relative md:block md:min-w-[500px] md:mx-0 md:max-w-none"
    >
        <div class="relative h-[500px] w-full xl:h-full">
            <!-- Card backgrounds -->
            <img
                src="https://wishlisted.s3.amazonaws.com/images/backgrounds/hero-bg-card-tilted.avif"
                alt=""
                aria-hidden="true"
                class="absolute inset-0 h-full w-full rotate-[-4.95deg] object-contain"
            />
            <!-- Video -->
            <div
                class="absolute inset-0 flex items-center justify-center overflow-hidden"
            >
                <div
                    class="relative h-full w-full origin-center rotate-[-4.95deg] scale-[1.1]"
                >
                    <video
                        :src="activeGame.video"
                        :key="activeGame.title"
                        autoplay
                        loop
                        muted
                        playsinline
                        class="h-full rotate-[-4.95deg] w-full object-cover transition-opacity duration-500"
                    />
                    <div
                        class="absolute bottom-0 left-0 w-full h-[150px] bg-black"
                    ></div>
                </div>
            </div>
            <!-- Gradient overlay -->
            <div
                class="absolute inset-0 bg-linear-to-t from-black to-transparent"
            />

            <!-- Carousel + game info -->
            <div
                class="absolute top-auto left-1/2 bottom-[20px] flex w-[490px] -translate-x-1/2 flex-col gap-[32px]"
            >
                <Swiper
                    :slides-per-view="3"
                    :space-between="12"
                    :centered-slides="true"
                    :loop="true"
                    :slide-to-clicked-slide="true"
                    @swiper="onDesktopInit"
                    @slide-change="onSlideChange"
                    class="w-full"
                >
                    <SwiperSlide v-for="(game, index) in slides" :key="index">
                        <button
                            type="button"
                            class="relative h-[164px] w-[164px] shrink-0 cursor-pointer overflow-hidden border-3 border-black"
                        >
                            <img
                                :src="game.thumb"
                                :alt="game.title"
                                class="h-full w-full object-cover"
                            />
                            <div class="thumb-glare" />
                        </button>
                    </SwiperSlide>
                </Swiper>

                <!-- Game info + nav -->
                <div class="flex items-center justify-between">
                    <div class="flex w-[179px] flex-col items-start gap-[12px]">
                        <span
                            class="w-full text-left font-obviously font-bold text-[20px] leading-none text-primary transition-opacity duration-300"
                            :key="activeGame.title"
                        >
                            {{ activeGame.title }}
                        </span>
                        <span
                            class="w-full text-left font-obviously font-bold text-[12px] uppercase leading-[1.4] tracking-[-0.12px] text-white"
                        >
                            {{ activeGame.category }}
                        </span>
                    </div>
                    <div class="flex items-center gap-[8px]">
                        <ButtonCta square @click="goPrev">
                            <img
                                src="https://wishlisted.s3.amazonaws.com/images/arrows/arrow-right-black-bg.svg"
                                alt="Previous"
                                class="h-5 w-5 rotate-180"
                            />
                        </ButtonCta>
                        <ButtonCta square @click="goNext">
                            <img
                                src="https://wishlisted.s3.amazonaws.com/images/arrows/arrow-right-black-bg.svg"
                                alt="Next"
                                class="h-5 w-5"
                            />
                        </ButtonCta>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- ===== MOBILE: carousel between text and buttons ===== -->
    <div class="relative z-3 flex w-full py-20 flex-col items-center md:hidden">
        <!-- Background element -->
        <img
            src="https://wishlisted.s3.amazonaws.com/images/backgrounds/hero-bg-element-mobile.svg"
            alt=""
            aria-hidden="true"
            class="pointer-events-none absolute inset-0 h-full w-full object-cover"
        />

        <!-- Carousel -->
        <Swiper
            :slides-per-view="3"
            :space-between="8"
            :centered-slides="true"
            :loop="true"
            :slide-to-clicked-slide="true"
            @swiper="onMobileInit"
            @slide-change="onSlideChange"
            class="w-full flex justify-center"
        >
            <SwiperSlide v-for="(game, index) in slides" :key="index">
                <button
                    type="button"
                    class="relative h-[131px] w-[131px] shrink-0 cursor-pointer overflow-hidden border-3 border-black"
                >
                    <img
                        :src="game.thumb"
                        :alt="game.title"
                        class="h-full w-full object-cover"
                    />
                    <div class="thumb-glare" />
                </button>
            </SwiperSlide>
        </Swiper>

        <!-- Game info -->
        <div
            class="relative mt-[20px] flex w-[338px] items-center justify-center"
        >
            <div class="flex w-[179px] flex-col items-center gap-[12px]">
                <span
                    class="w-full text-center font-obviously font-bold text-[16px] leading-none text-primary transition-opacity duration-300"
                    :key="activeGame.title"
                >
                    {{ activeGame.title }}
                </span>
                <span
                    class="w-full text-center font-obviously font-bold text-[10px] uppercase leading-[1.4] tracking-[-0.1px] text-white"
                >
                    {{ activeGame.category }}
                </span>
            </div>
        </div>
    </div>
</template>

<style scoped>
.thumb-glare {
    pointer-events: none;
    position: absolute;
    left: -3px;
    top: -3px;
    width: 217px;
    height: 290px;
    opacity: 0.2;
    mix-blend-mode: screen;
    background: linear-gradient(
        229.13deg,
        rgb(255, 255, 255) 21.18%,
        rgba(255, 255, 255, 0) 51.37%
    );
}
</style>
